import React, { FC, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { CustomModalActionType } from "@green0meter/api-client";

import {
  CurrentFeatureTooltip,
  StyledTableCell,
  StyledTableHeadCell,
  StyledTableWrapper,
} from "./AccountFeatures.styled";
import { FEATURES_TABLE } from "./AccountFeatures.const";

import {
  Card,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Stack,
  Typography,
} from "@components/base";
import { GetInTouchModal } from "@components/common";
import { useGetCustomModals } from "@api";

const AccountFeatures: FC = () => {
  const { t } = useTranslation();
  const { customModals } = useGetCustomModals();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Card>
        <Stack gap={3} p={3}>
          <Stack gap={1}>
            <Typography variant="h5" fontWeight={500} textAlign="center">
              {t("account.featuresTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              {t("account.featuresHeadline")}
            </Typography>
          </Stack>
          <StyledTableWrapper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell />
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableHeadCell key={feature.key} align="center">
                        <CurrentFeatureTooltip
                          title={t("account.featuresCurrent")}
                          open={true}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="textPrimary"
                          >
                            {feature.title}
                          </Typography>
                        </CurrentFeatureTooltip>
                      </StyledTableHeadCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresDataCollection")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {feature.dataCollection}
                        </Typography>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresReportsGeneration")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasReportGeneration ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresDashboards")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasDashboards ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresDMA")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasDoubleMateriality ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresEU")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasEUTaxonomy ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresRecommendations")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasDecarbonisationRecommendations ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t("account.featuresAudit")}
                      </Typography>
                    </StyledTableCell>
                    {FEATURES_TABLE.map((feature) => (
                      <StyledTableCell key={feature.key} align="center">
                        {feature.hasAudit ? (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </StyledTableWrapper>
          <Stack alignItems="center" gap={1} pt={2}>
            <Typography variant="h5" fontWeight={500}>
              {t("account.featuresGetInTouchTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("account.featuresGetInTouchText")}
            </Typography>
            <Box mt={2}>
              <Button onClick={handleOpenModal} variant="contained">
                {t("account.featuresGetInTouchCTA")}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Card>
      <GetInTouchModal
        open={isModalOpen}
        data={customModals?.find(
          (modal) => modal.actionType === CustomModalActionType.GetInTouchGeneral,
        )}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default AccountFeatures;
